import React, { useEffect, useRef } from 'react';
import JsBarcode from 'jsbarcode';
import { QRCodeSVG } from 'qrcode.react';

const BRAND = {
  primary:   '#0c7c4a',
  primaryLt: '#d1fae5',
  accent:    '#0e7490',
  amber:     '#f59e0b',
  amberLt:   '#fffbeb',
  white:     '#ffffff',
  dark:      '#111827',
  mid:       '#4b5563',
  muted:     '#9ca3af',
  border:    '#e5e7eb',
};

export const SIZE_MAP = {
  '40x30': { w: 151, h: 113 },
  '50x75': { w: 189, h: 283 },
  '58x40': { w: 219, h: 151 },
  '50x25': { w: 189, h:  94 },
  '80x50': { w: 302, h: 189 },
};

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getFullYear()).slice(2)}`;
}

export default function LabelCanvas({
  storeName       = '',
  productName     = '',
  weightLabel     = '',
  mrp             = '',
  barcodeValue    = '',
  barcodeFormat   = 'CODE128',
  mfgDate         = '',
  expiryDate      = '',
  expiryLabelType = 'expiry',
  labelSize       = '58x40',
  showStoreName   = true,
  customW         = null,
  customH         = null,
}) {
  const barcodeRef = useRef(null);
  const base = SIZE_MAP[labelSize] || SIZE_MAP['58x40'];
  const w = customW != null ? Math.round(customW) : base.w;
  const h = customH != null ? Math.round(customH) : base.h;
  const isQR = barcodeFormat === 'QR';

  const topH     = 4;
  const isNarrow = h < 100;
  const botH     = isNarrow ? 10 : 14;
  const contentH = h - topH - botH;

  const padX = Math.round(w * 0.05);
  const padY = Math.round(contentH * (isNarrow ? 0.03 : 0.04));
  const gap  = Math.round(contentH * (isNarrow ? 0.015 : 0.018));
  const bcH  = Math.max(14, contentH * (isNarrow ? 0.19 : 0.21));
  const mrpH = Math.max(14, contentH * (isNarrow ? 0.19 : 0.20));

  const fs = {
    store:  Math.max(5,  w * 0.038),
    name:   Math.max(7,  w * (isNarrow ? 0.053 : 0.058)),
    weight: Math.max(4,  w * 0.032),
    mrpLbl: Math.max(4,  w * 0.030),
    mrpVal: Math.max(9,  w * (isNarrow ? 0.076 : 0.082)),
    rupee:  Math.max(5,  w * 0.046),
    bc:     Math.max(4,  w * 0.029),
    date:   Math.max(4,  w * 0.029),
    footer: isNarrow ? 4.5 : 5.5,
  };

  const mrpNum     = parseFloat(mrp);
  const mrpDisplay = !isNaN(mrpNum) ? mrpNum.toFixed(2) : (mrp || '');
  const expiryPrefix = expiryLabelType === 'bestbefore' ? 'BB' : 'Exp';
  const dateStr    = [
    mfgDate    ? `Mfg: ${fmtDate(mfgDate)}`                   : '',
    expiryDate ? `${expiryPrefix}: ${fmtDate(expiryDate)}` : '',
  ].filter(Boolean).join('   ');

  useEffect(() => {
    if (!barcodeRef.current || isQR || !barcodeValue) return;
    try {
      JsBarcode(barcodeRef.current, barcodeValue, {
        format:       barcodeFormat,
        width:        Math.max(1, w * 0.009),
        height:       bcH,
        displayValue: false,
        margin:       0,
        background:   BRAND.white,
        lineColor:    BRAND.dark,
      });
    } catch { /* invalid value for this format */ }
  }, [barcodeValue, barcodeFormat, isQR, bcH, w]);

  return (
    <div style={{
      width: w, height: h,
      background: BRAND.white,
      borderRadius: 4,
      overflow: 'hidden',
      boxSizing: 'border-box',
      fontFamily: "'Segoe UI', Arial, sans-serif",
      display: 'flex',
      flexDirection: 'column',
      border: `1px solid ${BRAND.border}`,
      boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    }}>

      {/* ── Top accent strip (green → teal) ── */}
      <div style={{
        height: topH,
        background: `linear-gradient(90deg, ${BRAND.primary} 0%, ${BRAND.accent} 100%)`,
        flexShrink: 0,
      }} />

      {/* ── Content body ── */}
      <div style={{
        height: contentH,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        padding: `${padY}px ${padX}px`,
        gap: gap,
        overflow: 'hidden',
        flexShrink: 0,
      }}>

        {/* Store name */}
        {showStoreName && storeName && (
          <div style={{
            fontSize: fs.store,
            fontWeight: 700,
            color: BRAND.primary,
            textAlign: 'center',
            textTransform: 'uppercase',
            letterSpacing: Math.max(0.4, w * 0.004),
            lineHeight: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            paddingBottom: Math.round(contentH * 0.012),
            borderBottom: `0.5px solid ${BRAND.border}`,
            flexShrink: 0,
          }}>
            {storeName}
          </div>
        )}

        {/* Product name */}
        <div style={{
          fontSize: fs.name,
          fontWeight: 700,
          color: BRAND.dark,
          textAlign: 'center',
          lineHeight: 1.2,
          wordBreak: 'break-word',
          flexShrink: 0,
        }}>
          {productName || (
            <span style={{ color: BRAND.muted, fontStyle: 'italic', fontWeight: 400 }}>Product Name</span>
          )}
        </div>

        {/* Weight / qty pill */}
        {weightLabel && (
          <div style={{ display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
            <div style={{
              border: `1px solid ${BRAND.amber}`,
              borderRadius: 999,
              padding: `1px ${Math.round(w * 0.038)}px`,
              fontSize: fs.weight,
              fontWeight: 600,
              color: '#92400e',
              background: BRAND.amberLt,
              letterSpacing: 0.3,
            }}>
              {weightLabel}
            </div>
          </div>
        )}

        {/* MRP box */}
        {mrpDisplay && (
          <div style={{
            background: BRAND.primaryLt,
            border: `1.5px solid ${BRAND.primary}55`,
            borderRadius: 4,
            height: mrpH,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
            flexShrink: 0,
          }}>
            <span style={{ fontSize: fs.mrpLbl, fontWeight: 700, color: BRAND.primary, letterSpacing: 0.5, textTransform: 'uppercase' }}>
              MRP
            </span>
            <span style={{ fontSize: fs.rupee, fontWeight: 700, color: BRAND.primary, lineHeight: 1 }}>
              ₹
            </span>
            <span style={{ fontSize: fs.mrpVal, fontWeight: 900, color: BRAND.primary, lineHeight: 1, letterSpacing: -0.5 }}>
              {mrpDisplay}
            </span>
          </div>
        )}

        {/* Barcode */}
        {barcodeValue && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, flexShrink: 0, width: '100%' }}>
            {isQR ? (
              <QRCodeSVG
                value={barcodeValue}
                size={Math.min(w * 0.28, contentH * 0.28)}
                level="M"
                fgColor={BRAND.dark}
              />
            ) : (
              <svg ref={barcodeRef} style={{ maxWidth: '90%', height: bcH }} />
            )}
            <span style={{ fontSize: fs.bc, fontFamily: 'monospace', color: BRAND.mid, letterSpacing: 1.5 }}>
              {barcodeValue}
            </span>
          </div>
        )}

        {/* Mfg / Expiry */}
        {dateStr && (
          <div style={{ fontSize: fs.date, color: BRAND.muted, textAlign: 'center', lineHeight: 1.1, flexShrink: 0 }}>
            {dateStr}
          </div>
        )}
      </div>

      {/* ── Bottom accent strip (teal → green) — "Powered by KiranaFlow" ── */}
      <div style={{
        height: botH,
        background: `linear-gradient(90deg, ${BRAND.accent} 0%, ${BRAND.primary} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        <span style={{
          fontSize: fs.footer,
          color: 'rgba(255,255,255,0.85)',
          fontStyle: 'italic',
          letterSpacing: 0.3,
        }}>
          Powered by KiranaFlow
        </span>
      </div>
    </div>
  );
}
